import { FC, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
	Alert,
	Box,
	Button,
	ButtonDropdown,
	Container,
	ContentLayout,
	Grid,
	Header,
	SpaceBetween
} from '@cloudscape-design/components'

import HeaderWithStatusBadges from '../../../components/Common/HeaderWithStatusBadges'
import LoadingSpinner from '../../../components/LoadingSpinner'
import ApprovalForm from '../../../components/Account/ApprovalForm'

import useSingleAccount from '../../../hooks/query/useSingleAccount'
import { KeyValuePairs } from '@aws-northstar/ui'

import AccountNetworkReadonlyView from '../../../components/Account/AccountNetworkReadonlyView'
import RejectionForm from '../../../components/Account/RejectionForm'

import EditGenericTags from '../../../components/ControlledFormInputs/GenericTagsEdit'
import ReadGenericTags from '../../../components/ControlledFormInputs/GenericTagsRead'

import RequiredPermissions from '../../../helper/RequiredPermissions'
import CONSTANTS from '../../../constants'

import useUserPermissions from '../../../hooks/query/useUserPermissions'
import { isStgOnboardingSourceAccountType } from '../../../helper/stgOnboardingPrefill'
import { getAccountStgPath } from '../../../helper/accountRoutes'
import hasAllPermissions from '../../../helper/hasRequiredPermissions'
import getPlatformOwnerPermissions from '../../../helper/platformOwnerPermissions'

const extractUserNamesFromObject = (
	SSOUserNames?: Array<{ displayName?: string; userName: string }> | string
): string[] => {
	if (!SSOUserNames) return []
	if (typeof SSOUserNames === 'string') {
		return SSOUserNames.split(',').map((s) => s.trim()).filter(Boolean)
	}
	return SSOUserNames.map((user) =>
		user.displayName ? `${user.userName} (${user.displayName})` : user.userName
	)
}

const AccountDetails: FC = () => {
	const CURRENT_USER_ID = JSON.parse(localStorage['okta-token-storage'])[
		'idToken'
	]['claims']['email']
	const {
		data: userPermissions
	} = useUserPermissions(CURRENT_USER_ID)
	const hasSpecificPermissions = (
		permissions: string[],
		requiredPermissions: string[]
	): boolean => {
		return requiredPermissions.some((requiredPerm) =>
			permissions.includes(requiredPerm)
		)
	}
	const specificPermissions = ['accounts:*:*', 'accounts:*:approve', '*:*:*']
	const hasRequiredPermissions =
		userPermissions &&
		hasSpecificPermissions(userPermissions.permissions, specificPermissions)
	const { id: accountRequestID } = useParams() as { id: string }
	const platformOwnerResource = {
		type: 'accounts' as const,
		id: accountRequestID
	}
	const hasPlatformOwnerPermissions = Boolean(
		userPermissions &&
		hasAllPermissions(
			getPlatformOwnerPermissions(platformOwnerResource),
			userPermissions.permissions
		)
	)
	const canReadAccountTags = Boolean(
		userPermissions &&
		(hasAllPermissions(CONSTANTS.ACCOUNT_REQUESTOR, userPermissions.permissions) ||
			hasPlatformOwnerPermissions)
	)
	const navigate = useNavigate()
	const { data: thisAccount, isLoading } = useSingleAccount(
		accountRequestID,
		true
	)

	const [showApprovalForm, setShowApprovalForm] = useState(false)
	const [showRejectionForm, setShowRejectionForm] = useState(false)

	if (!thisAccount || isLoading) {
		return (
			<ContentLayout
				header={
					<HeaderWithStatusBadges
						headerName=""
						isLoadingApprovals
						skeletonBadgeCount={4}
						navigateTo="/accounts"
					/>
				}
			>
				<LoadingSpinner />
			</ContentLayout>
		)
	}

	if (thisAccount.status === 'ERROR') {
		return (
			<Alert
				statusIconAriaLabel="Error"
				type="error"
				header="An error occurred"
			>
				{thisAccount.details.message}
			</Alert>
		)
	}

	const keyValuePairsItems = [
		[
			{ label: 'Requestor ID', value: thisAccount?.requestorID },
			{ label: 'Account Type', value: thisAccount?.type },
			{
				label: 'Account workload type',
				value: thisAccount?.details?.accountWorkloadType ?? '-'
			},
			{
				label: 'SSO User Names',
				value: extractUserNamesFromObject(thisAccount?.details.SSOUserNames).join(', ') || '—'
			}
		],
		[
			{ label: 'Platform Owner', value: thisAccount?.details.platformOwner },
			{ label: 'Distribution List', value: thisAccount?.details.distributionList },
			{
				label: 'Budget Threshold Alert',
				value: thisAccount?.details.monthlyBudgetCap
			}
		]
	]

	let isAllTagsApproved = false
	if (thisAccount?.tags !== undefined) {
		isAllTagsApproved = true
		let accountTags = thisAccount?.tags
		let tagsApprovalStatuses = accountTags.approvalStatus
		for (const tagStatus of tagsApprovalStatuses) {
			if (tagStatus.status !== 1) {
				isAllTagsApproved = false
			}
		}
	}

	const showStgAdditionalResources =
		thisAccount.status === 'APPROVED' &&
		isStgOnboardingSourceAccountType(thisAccount.type) &&
		thisAccount.stgRequest?.status !== 'PENDING_APPROVAL'

	const showStgPendingRightColumn =
		thisAccount.status === 'APPROVED' &&
		thisAccount.stgRequest?.status === 'PENDING_APPROVAL'

	const useAccountDetailGrid = showStgAdditionalResources || showStgPendingRightColumn

	const accountMainColumn = (
		<SpaceBetween direction="vertical" size="l">
			<Container header={<Header variant="h3">Request Details</Header>}>
				<KeyValuePairs items={keyValuePairsItems} />
			</Container>
					{!hasRequiredPermissions &&
						thisAccount.status === 'PENDING_APPROVAL' &&
						canReadAccountTags && (
							<Container header={<Header variant="h3">Tags Details</Header>}>
								{thisAccount.tagsID && (
									<ReadGenericTags
										tagsId={thisAccount.tagsID}
										platformOwnerResource={platformOwnerResource}
										allowPlatformOwnerApproval={true}
									/>
								)}
							</Container>
						)}
					{(thisAccount.status === 'APPROVED' ||
						thisAccount.status === 'REJECTED') && (
						<RequiredPermissions
							requiredPermissions={CONSTANTS.ACCOUNT_REQUESTOR}
						>
							<Container header={<Header variant="h3">Tags Details</Header>}>
								{thisAccount.tagsID && (
									<ReadGenericTags tagsId={thisAccount.tagsID} />
								)}
							</Container>
						</RequiredPermissions>
					)}
					{/* {(thisAccount.status === 'APPROVED' ||
                        thisAccount.status === 'REJECTED') && (
                        <RequiredPermissions
                            requiredPermissions={CONSTANTS.ACCOUNT_APPROVER}
                        >
                            <Container header={<Header variant="h3">Tags Details</Header>}>
                                {thisAccount.tagsID && (
                                    <ReadGenericTags tagsId={thisAccount.tagsID}/>
                                )}
                            </Container>
                        </RequiredPermissions>
                    )} */}
					{thisAccount.status === 'PENDING_APPROVAL' &&
						hasRequiredPermissions && (
							<RequiredPermissions
								requiredPermissions={CONSTANTS.ACCOUNT_APPROVER}
							>
								<Container header={<Header variant="h3">Tags Details</Header>}>
									{thisAccount.tagsID && (
										<EditGenericTags
											tagsId={thisAccount.tagsID}
											platformOwnerResource={platformOwnerResource}
										/>
									)}
								</Container>
							</RequiredPermissions>
						)}
					{thisAccount.status === 'PENDING_APPROVAL' &&
						CURRENT_USER_ID !== thisAccount.requestorID && (
							<RequiredPermissions
								requiredPermissions={CONSTANTS.ACCOUNT_APPROVER}
							>
								<Box float="right">
									<ButtonDropdown
										items={
											isAllTagsApproved
												? [
														{ text: 'To Approve...', id: 'to-approve' },
														{ text: 'To Reject...', id: 'to-reject' }
												  ]
												: [{ text: 'To Reject...', id: 'to-reject' }]
										}
										onItemClick={({ detail }) => {
											if (detail.id === 'to-approve') {
												setShowApprovalForm(true)
												setShowRejectionForm(false)
											}

											if (detail.id === 'to-reject') {
												setShowApprovalForm(false)
												setShowRejectionForm(true)
											}
										}}
										variant="primary"
									>
										Action
									</ButtonDropdown>
								</Box>
							</RequiredPermissions>
						)}

					{showApprovalForm && (
						<ApprovalForm
							thisAccountRequestID={thisAccount.accountRequestID}
							shouldShowApprovalForm={setShowApprovalForm}
						/>
					)}

					{showRejectionForm && (
						<RejectionForm
							thisAccountRequestID={thisAccount.accountRequestID}
							shouldShowRejectionForm={setShowRejectionForm}
						/>
					)}

					{thisAccount.status === 'APPROVED' && (
						<AccountNetworkReadonlyView
							details={
								(thisAccount.approval?.details ??
									thisAccount.approval) as Record<string, unknown>
							}
							accountType={thisAccount.type}
							networkSectionTitle="Approved network details"
							networkSectionHeaderVariant="h3"
							ipTableHeaderVariant="h3"
						/>
					)}
		</SpaceBetween>
	)

	return (
		<ContentLayout
			header={
				<HeaderWithStatusBadges
					headerName={thisAccount?.details.projectName}
					status={thisAccount?.status}
					approvalStatus={thisAccount?.tags?.approvalStatus || []}
					navigateTo="/accounts"
				/>
			}
		>
			{useAccountDetailGrid ? (
				<Grid
					gridDefinition={[
						{ colspan: { default: 3, xxs: 9 } },
						{ colspan: { default: 9, xxs: 3 } }
					]}
				>
					{accountMainColumn}
					<SpaceBetween direction="vertical" size="l">
						{showStgPendingRightColumn && (
							<Container header={<Header variant="h3">Stage network — pending approval</Header>}>
								<Box padding={{ vertical: 's', horizontal: 'xs' }}>
									<Alert
										type="info"
										header="Awaiting approver"
										action={
											<Button
												variant="primary"
												wrapText={false}
												onClick={() => navigate(getAccountStgPath(accountRequestID))}
											>
												Review stage network
											</Button>
										}
									/>
								</Box>
							</Container>
						)}
						{showStgAdditionalResources &&
							thisAccount.stgRequest?.status === 'APPROVED' && (
								<Container header={<Header variant="h3">Stage environment</Header>}>
									<Box padding={{ vertical: 's', horizontal: 'xs' }}>
										<Alert
											type="success"
											header="Stage network approved"
											action={
												<Button
													variant="primary"
													wrapText={false}
													onClick={() =>
														navigate(getAccountStgPath(accountRequestID))
													}
												>
													View stage network
												</Button>
											}
										>
											Staging network details are available for review.
										</Alert>
									</Box>
								</Container>
							)}
						{showStgAdditionalResources &&
							thisAccount.stgRequest?.status !== 'APPROVED' && (
								<Container header={<Header variant="h3">Stage environment</Header>}>
									<SpaceBetween direction="vertical" size="s">
										<Button
											variant="primary"
											onClick={() =>
												navigate(
													`/accounts/new?fromAccount=${encodeURIComponent(
														accountRequestID
													)}&stg=1`
												)
											}
										>
											Request stage environment
										</Button>
									</SpaceBetween>
								</Container>
							)}
					</SpaceBetween>
				</Grid>
			) : (
				accountMainColumn
			)}
		</ContentLayout>
	)
}

export default AccountDetails
